/**
 * Copyright (c) 2025 Kalpit Patel
 *
 * Sole Developer and Owner:
 * Kalpit Patel is the sole developer and creator of this entire application.
 * No other individual or entity has contributed to the development of this software.
 * All rights reserved.
 *
 * This file is part of AksharJobs application.
 */

import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faUsers,
  faGraduationCap,
  faBriefcase,
  faEnvelope,
  faGlobe,
  faArrowLeft,
  faPhone,
  faMapMarkerAlt,
  faEdit,
  faSave,
  faInfoCircle,
  faCamera,
  faImage,
  faBuilding
} from '@fortawesome/free-solid-svg-icons';
import {
  faLinkedin,
  faFacebook,
  faTwitter,
  faInstagram
} from '@fortawesome/free-brands-svg-icons';
import axios from 'axios';
import { buildApiUrl } from '../config/api';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../components/Toast';

// NOTE: This component copies the layout and design patterns from UniversityMyProfile.jsx,
// but uses association-specific content and data model.

const ASSOCIATION_TYPES = [
  "Professional Association",
  "Industry Association",
  "Trade Association",
  "Business Association",
  "Chamber of Commerce",
  "Employer Association",
  "Regional Association",
  "National Association",
  "International Association",
  "Other"
];

const AssociationMyProfile = () => {
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const { showSuccess, showError, showWarning } = useToast();

  const [editingSections, setEditingSections] = useState({
    association: false,
    contact: false,
    person: false,
    details: false,
    industries: false,
    social: false
  });

  const [saving, setSaving] = useState(false);
  const [profileCompletionPercentage, setProfileCompletionPercentage] = useState(0);
  const [associationLogoFile, setAssociationLogoFile] = useState(null);
  const [associationLogoPreview, setAssociationLogoPreview] = useState(null);
  const associationLogoInputRef = useRef(null);

  const [profileForm, setProfileForm] = useState({
    // Association Information (from registration form)
    associationName: '',
    associationType: '',
    yearEstablished: '',
    registrationNumber: '',
    associationLogo: '',
    showLogoOnWebsite: true,

    // Contact Information (from registration form)
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    country: '',
    postalCode: '',
    website: '',

    // Primary Contact Person
    contactName: '',
    designation: '',
    contactEmail: '',
    contactPhone: '',

    // Association Details
    totalMembers: '',
    totalStaff: '',
    accreditation: '',
    accreditationNumber: '',
    industriesServed: [],
    memberBenefits: '',
    about: '',

    // Social Media & Additional Information
    linkedin: '',
    facebook: '',
    twitter: '',
    instagram: ''
  });

  // Compute primary location for display
  const primaryLocation = useMemo(() => {
    const parts = [];
    if (profileForm.city) parts.push(profileForm.city);
    if (profileForm.state) parts.push(profileForm.state);
    if (profileForm.country) parts.push(profileForm.country);
    return parts.length > 0 ? parts.join(', ') : null;
  }, [profileForm.city, profileForm.state, profileForm.country]);

  // Load association profile from backend
  useEffect(() => {
    const loadAssociationProfile = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) return;

        const response = await axios.get(
          buildApiUrl('/api/user/profile'),
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (response.data) {
          const data = response.data;

          setProfileForm(prev => ({
            ...prev,
            associationName: data.associationName || prev.associationName,
            associationType: data.associationType || prev.associationType,
            yearEstablished: data.yearEstablished || prev.yearEstablished,
            registrationNumber: data.registrationNumber || prev.registrationNumber,

            email: data.email || data.officialEmail || prev.email,
            phone: data.phone || data.phoneNumber || prev.phone,
            address: data.address || data.physicalAddress || prev.address,
            city: data.city || prev.city,
            state: data.state || prev.state,
            country: data.country || prev.country,
            postalCode: data.postalCode || prev.postalCode,
            website: data.website || data.websiteUrl || prev.website,

            contactName: data.contactName || data.contactPersonName || prev.contactName,
            designation: data.designation || data.contactPersonTitle || prev.designation,
            contactEmail: data.contactEmail || data.contactPersonEmail || prev.contactEmail,
            contactPhone: data.contactPhone || data.contactPersonPhone || prev.contactPhone,

            totalMembers: data.totalMembers || prev.totalMembers,
            totalStaff: data.totalStaff || prev.totalStaff,
            accreditation: data.accreditation || prev.accreditation,
            accreditationNumber: data.accreditationNumber || prev.accreditationNumber,
            industriesServed: Array.isArray(data.industriesServed)
              ? data.industriesServed
              : (data.industriesServed ? [data.industriesServed] : []),
            memberBenefits: data.memberBenefits || prev.memberBenefits,
            about: data.about || data.associationDescription || prev.about,

            linkedin: data.linkedin || data.linkedinProfile || prev.linkedin,
            facebook: data.facebook || data.facebookPage || prev.facebook,
            twitter: data.twitter || data.twitterHandle || prev.twitter,
            instagram: data.instagram || prev.instagram,
            showLogoOnWebsite: (typeof data.showLogoOnWebsite === 'boolean') ? data.showLogoOnWebsite : (prev.showLogoOnWebsite ?? true),
            associationLogo: data.associationLogo || data.companyLogo || prev.associationLogo
          }));

          // Load association logo preview
          if (data.associationLogo || data.companyLogo) {
            let logoPath = data.associationLogo || data.companyLogo;
            let normalizedPath = logoPath.replace(/\\/g, '/');
            let fullUrl = '';
            if (normalizedPath.startsWith('uploads/')) {
              const uploadPath = normalizedPath.replace('uploads/', '');
              fullUrl = buildApiUrl(`/api/users/uploads/${uploadPath}`);
            } else if (normalizedPath.startsWith('/uploads/')) {
              const uploadPath = normalizedPath.replace('/uploads/', '');
              fullUrl = buildApiUrl(`/api/users/uploads/${uploadPath}`);
            } else if (normalizedPath.startsWith('http://') || normalizedPath.startsWith('https://')) {
              fullUrl = normalizedPath;
            } else {
              fullUrl = buildApiUrl(`/api/users/uploads/${normalizedPath}`);
            }
            setAssociationLogoPreview(fullUrl);
          }

          // Calculate profile completion
          let completion = 0;
          if (typeof data.profileCompletion === 'number') {
            completion = data.profileCompletion;
          } else {
            const requiredFields = [
              'associationName',
              'associationType',
              'registrationNumber',
              'email',
              'phone',
              'address',
              'city',
              'state',
              'country',
              'contactName',
              'designation',
              'contactEmail',
              'contactPhone'
            ];
            
            const filledFields = requiredFields.filter(field => {
              const value = data[field];
              return value && value.toString().trim() !== '';
            }).length;
            
            const optionalFields = [
              'website',
              'totalMembers',
              'totalStaff',
              'accreditation',
              'accreditationNumber',
              'industriesServed',
              'memberBenefits',
              'about'
            ];
            
            const filledOptional = optionalFields.filter(field => {
              const value = data[field];
              if (field === 'industriesServed') {
                return Array.isArray(value) ? value.length > 0 : (value && value.toString().trim() !== '');
              }
              return value && value.toString().trim() !== '';
            }).length;
            
            const requiredPercentage = (filledFields / requiredFields.length) * 70;
            const optionalPercentage = (filledOptional / optionalFields.length) * 30;
            completion = Math.round(requiredPercentage + optionalPercentage);
            
            if (data.profileCompleted || data.hasCompletedProfile) {
              completion = 100;
            }
          }
          
          setProfileCompletionPercentage(Math.max(0, Math.min(100, completion)));
        }
      } catch (error) {
        console.error('Error loading association profile:', error);
      }
    };

    loadAssociationProfile();
  }, []);

  // Recalculate profile completion when profileForm changes
  useEffect(() => {
    const calculateCompletion = () => {
      const requiredFields = [
        'associationName',
        'associationType',
        'registrationNumber',
        'email',
        'phone',
        'address',
        'city',
        'state',
        'country',
        'contactName',
        'designation',
        'contactEmail',
        'contactPhone'
      ];
      
      const filledFields = requiredFields.filter(field => {
        const value = profileForm[field];
        return value && value.toString().trim() !== '';
      }).length;
      
      const optionalFields = [
        'website',
        'totalMembers',
        'totalStaff',
        'accreditation',
        'accreditationNumber',
        'industriesServed',
        'memberBenefits',
        'about'
      ];
      
      const filledOptional = optionalFields.filter(field => {
        const value = profileForm[field];
        if (field === 'industriesServed') {
          return Array.isArray(value) ? value.length > 0 : (value && value.toString().trim() !== '');
        }
        return value && value.toString().trim() !== '';
      }).length;
      
      const requiredPercentage = (filledFields / requiredFields.length) * 70;
      const optionalPercentage = (filledOptional / optionalFields.length) * 30;
      const completion = Math.round(requiredPercentage + optionalPercentage);
      
      setProfileCompletionPercentage(Math.max(0, Math.min(100, completion)));
    };

    if (profileForm.associationName || profileForm.email) {
      calculateCompletion();
    }
  }, [profileForm]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setProfileForm((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const toggleSectionEdit = (section) => {
    setEditingSections((prev) => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const handleAssociationLogoChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        showWarning('File size must be less than 5MB');
        e.target.value = '';
        return;
      }
      if (!file.type.match(/^image\/(png|jpg|jpeg)$/i)) {
        showWarning('Only PNG and JPG images are allowed');
        e.target.value = '';
        return;
      }
      setAssociationLogoFile(file);
      const reader = new FileReader();
      reader.onload = (e) => setAssociationLogoPreview(e.target.result);
      reader.readAsDataURL(file);
      
      // Auto-save the logo immediately (like InternMyProfile does)
      try {
        setSaving(true);
        const token = localStorage.getItem('token');
        if (!token) {
          showWarning('Please log in to upload logo');
          return;
        }
        
        const formData = new FormData();
        formData.append('associationLogo', file);
        
        // Include all profile data to ensure the save works
        Object.keys(profileForm).forEach(key => {
          const value = profileForm[key];
          if (value !== null && value !== undefined) {
            if (typeof value === 'object' && !Array.isArray(value)) {
              formData.append(key, JSON.stringify(value));
            } else if (Array.isArray(value)) {
              formData.append(key, JSON.stringify(value));
            } else {
              formData.append(key, value);
            }
          }
        });
        
        const response = await axios.put(
          buildApiUrl('/api/user/profile'),
          formData,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'multipart/form-data'
            }
          }
        );
        
        if (response.status === 200 || response.status === 201) {
          // Clear the logo file after successful upload
          setAssociationLogoFile(null);
          if (associationLogoInputRef.current) {
            associationLogoInputRef.current.value = '';
          }
          
          // Update logo path from response
          if (response.data) {
            const logoPath = response.data.associationLogo || response.data.companyLogo;
            if (logoPath) {
              let normalizedPath = logoPath.replace(/\\/g, '/');
              let fullUrl = '';
              if (normalizedPath.startsWith('uploads/')) {
                const uploadPath = normalizedPath.replace('uploads/', '');
                fullUrl = buildApiUrl(`/api/users/uploads/${uploadPath}`);
              } else if (normalizedPath.startsWith('/uploads/')) {
                const uploadPath = normalizedPath.replace('/uploads/', '');
                fullUrl = buildApiUrl(`/api/users/uploads/${uploadPath}`);
              } else if (normalizedPath.startsWith('http://') || normalizedPath.startsWith('https://')) {
                fullUrl = normalizedPath;
              } else {
                fullUrl = buildApiUrl(`/api/users/uploads/${normalizedPath}`);
              }
              setProfileForm(prev => ({ ...prev, associationLogo: logoPath }));
              setAssociationLogoPreview(fullUrl);
            }
          }
          
          // Reload profile to get updated data
          const refreshResponse = await axios.get(
            buildApiUrl('/api/user/profile'),
            { headers: { Authorization: `Bearer ${token}` } }
          );
          if (refreshResponse.data) {
            const data = refreshResponse.data;
            if (data.associationLogo || data.companyLogo) {
              let logoPath = data.associationLogo || data.companyLogo;
              let normalizedPath = logoPath.replace(/\\/g, '/');
              let fullUrl = '';
              if (normalizedPath.startsWith('uploads/')) {
                const uploadPath = normalizedPath.replace('uploads/', '');
                fullUrl = buildApiUrl(`/api/users/uploads/${uploadPath}`);
              } else if (normalizedPath.startsWith('/uploads/')) {
                const uploadPath = normalizedPath.replace('/uploads/', '');
                fullUrl = buildApiUrl(`/api/users/uploads/${uploadPath}`);
              } else if (normalizedPath.startsWith('http://') || normalizedPath.startsWith('https://')) {
                fullUrl = normalizedPath;
              } else {
                fullUrl = buildApiUrl(`/api/users/uploads/${normalizedPath}`);
              }
              setProfileForm(prev => ({ ...prev, associationLogo: logoPath }));
              setAssociationLogoPreview(fullUrl);
            }
          }
        }
      } catch (error) {
        console.error('Error uploading logo:', error);
        console.error('Error details:', error.response?.data);
        showError('Error uploading logo. Please try again.');
      } finally {
        setSaving(false);
      }
    }
  };

  const handleSave = async (section) => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        showWarning('Please log in to save changes');
        return;
      }

      // Prepare the data to save
      const dataToSave = {
        ...profileForm
      };

      let response;
      
      // If there's a logo file, use FormData
      if (associationLogoFile) {
        const formData = new FormData();
        formData.append('associationLogo', associationLogoFile);
        
        // Append all profile data
        Object.keys(dataToSave).forEach(key => {
          const value = dataToSave[key];
          if (value !== null && value !== undefined) {
            if (typeof value === 'object' && !Array.isArray(value)) {
              formData.append(key, JSON.stringify(value));
            } else if (Array.isArray(value)) {
              formData.append(key, JSON.stringify(value));
            } else {
              formData.append(key, value);
            }
          }
        });

        response = await axios.put(
          buildApiUrl('/api/user/profile'),
          formData,
          { 
            headers: { 
              Authorization: `Bearer ${token}`,
              'Content-Type': 'multipart/form-data'
            } 
          }
        );
        
        // Clear logo file after successful upload and update preview
        if (associationLogoFile && response.data) {
          const logoPath = response.data.associationLogo || response.data.companyLogo;
          if (logoPath) {
            let normalizedPath = logoPath.replace(/\\/g, '/');
            let fullUrl = '';
            if (normalizedPath.startsWith('uploads/')) {
              const uploadPath = normalizedPath.replace('uploads/', '');
              fullUrl = buildApiUrl(`/api/users/uploads/${uploadPath}`);
            } else if (normalizedPath.startsWith('/uploads/')) {
              const uploadPath = normalizedPath.replace('/uploads/', '');
              fullUrl = buildApiUrl(`/api/users/uploads/${uploadPath}`);
            } else if (normalizedPath.startsWith('http://') || normalizedPath.startsWith('https://')) {
              fullUrl = normalizedPath;
            } else {
              fullUrl = buildApiUrl(`/api/users/uploads/${normalizedPath}`);
            }
            setProfileForm(prev => ({ ...prev, associationLogo: logoPath }));
            setAssociationLogoPreview(fullUrl);
          }
          setAssociationLogoFile(null);
          if (associationLogoInputRef.current) {
            associationLogoInputRef.current.value = '';
          }
        }
      } else {
        // Regular JSON save
        response = await axios.put(
          buildApiUrl('/api/user/profile'),
          dataToSave,
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }

      if (response.data) {
        showSuccess(`${section} section saved successfully!`);
        toggleSectionEdit(section);
        // Reload profile to get updated data
        const refreshResponse = await axios.get(
          buildApiUrl('/api/user/profile'),
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (refreshResponse.data) {
          const data = refreshResponse.data;
          setProfileForm(prev => ({
            ...prev,
            associationName: data.associationName || prev.associationName,
            associationType: data.associationType || prev.associationType,
            yearEstablished: data.yearEstablished || prev.yearEstablished,
            registrationNumber: data.registrationNumber || prev.registrationNumber,
            email: data.email || data.officialEmail || prev.email,
            phone: data.phone || data.phoneNumber || prev.phone,
            address: data.address || data.physicalAddress || prev.address,
            city: data.city || prev.city,
            state: data.state || prev.state,
            country: data.country || prev.country,
            postalCode: data.postalCode || prev.postalCode,
            website: data.website || data.websiteUrl || prev.website,
            contactName: data.contactName || data.contactPersonName || prev.contactName,
            designation: data.designation || data.contactPersonTitle || prev.designation,
            contactEmail: data.contactEmail || data.contactPersonEmail || prev.contactEmail,
            contactPhone: data.contactPhone || data.contactPersonPhone || prev.contactPhone,
            totalMembers: data.totalMembers || prev.totalMembers,
            totalStaff: data.totalStaff || prev.totalStaff,
            accreditation: data.accreditation || prev.accreditation,
            accreditationNumber: data.accreditationNumber || prev.accreditationNumber,
            industriesServed: Array.isArray(data.industriesServed)
              ? data.industriesServed
              : (data.industriesServed ? [data.industriesServed] : []),
            memberBenefits: data.memberBenefits || prev.memberBenefits,
            about: data.about || data.associationDescription || prev.about,
            linkedin: data.linkedin || data.linkedinProfile || prev.linkedin,
            facebook: data.facebook || data.facebookPage || prev.facebook,
            twitter: data.twitter || data.twitterHandle || prev.twitter,
            instagram: data.instagram || prev.instagram,
            associationLogo: data.associationLogo || data.companyLogo || prev.associationLogo,
            showLogoOnWebsite: data.showLogoOnWebsite !== false
          }));
          
          // Load association logo preview
          if (data.associationLogo || data.companyLogo) {
            let logoPath = data.associationLogo || data.companyLogo;
            let normalizedPath = logoPath.replace(/\\/g, '/');
            let fullUrl = '';
            if (normalizedPath.startsWith('uploads/')) {
              const uploadPath = normalizedPath.replace('uploads/', '');
              fullUrl = buildApiUrl(`/api/users/uploads/${uploadPath}`);
            } else if (normalizedPath.startsWith('/uploads/')) {
              const uploadPath = normalizedPath.replace('/uploads/', '');
              fullUrl = buildApiUrl(`/api/users/uploads/${uploadPath}`);
            } else if (normalizedPath.startsWith('http://') || normalizedPath.startsWith('https://')) {
              fullUrl = normalizedPath;
            } else {
              fullUrl = buildApiUrl(`/api/users/uploads/${normalizedPath}`);
            }
            setAssociationLogoPreview(fullUrl);
          }
          
          // Recalculate profile completion after save
          const requiredFields = [
            'associationName',
            'associationType',
            'registrationNumber',
            'email',
            'phone',
            'address',
            'city',
            'state',
            'country',
            'contactName',
            'designation',
            'contactEmail',
            'contactPhone'
          ];
          
          const filledFields = requiredFields.filter(field => {
            const value = data[field];
            return value && value.toString().trim() !== '';
          }).length;
          
          const optionalFields = [
            'website',
            'totalMembers',
            'totalStaff',
            'accreditation',
            'accreditationNumber',
            'industriesServed',
            'memberBenefits',
            'about'
          ];
          
          const filledOptional = optionalFields.filter(field => {
            const value = data[field];
            if (field === 'industriesServed') {
              return Array.isArray(value) ? value.length > 0 : (value && value.toString().trim() !== '');
            }
            return value && value.toString().trim() !== '';
          }).length;
          
          const requiredPercentage = (filledFields / requiredFields.length) * 70;
          const optionalPercentage = (filledOptional / optionalFields.length) * 30;
          let completion = Math.round(requiredPercentage + optionalPercentage);
          
          if (data.profileCompleted || data.hasCompletedProfile) {
            completion = 100;
          }
          
          setProfileCompletionPercentage(Math.max(0, Math.min(100, completion)));
        }
      }
    } catch (error) {
      console.error('Error saving profile:', error);
      showError('Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const EditButton = ({ section }) => (
    !editingSections[section] ? (
      <button
        onClick={() => toggleSectionEdit(section)}
        style={{
          background: 'linear-gradient(135deg, #f97316 0%, #0d9488 100%)',
          color: 'white',
          border: 'none',
          padding: '8px 16px',
          borderRadius: '6px',
          fontSize: '14px',
          fontWeight: '600',
          cursor: 'pointer',
          transition: 'all 0.3s ease'
        }}
      >
        <FontAwesomeIcon icon={faEdit} /> Edit
      </button>
    ) : (
      <div style={{ display: 'flex', gap: '8px' }}>
        <button
          onClick={() => handleSave(section)}
          style={{
            background: '#10b981',
            color: 'white',
            border: 'none',
            padding: '8px 16px',
            borderRadius: '6px',
            fontSize: '14px',
            fontWeight: '600',
            cursor: 'pointer'
          }}
        >
          <FontAwesomeIcon icon={faSave} /> Save
        </button>
        <button
          type="button"
          onClick={() => toggleSectionEdit(section)}
          style={{
            background: '#dc2626',
            color: '#ffffff',
            border: 'none',
            padding: '8px 16px',
            borderRadius: '6px',
            fontSize: '14px',
            fontWeight: '600',
            cursor: 'pointer'
          }}
        >
          Cancel
        </button>
      </div>
    )
  );

  const containerStyle = {
    padding: '0 24px 32px 24px',
    maxWidth: '100%',
    width: '100%',
    margin: '0',
    minHeight: '100vh',
    boxSizing: 'border-box'
  };

  const elevatedCardStyle = {
    border: '1px solid #edf2f7',
    borderRadius: '16px',
    boxShadow: '0 10px 30px rgba(17, 24, 39, 0.06)',
    background: '#fff',
    marginBottom: '20px',
    padding: '25px',
    overflow: 'hidden'
  };

  const profileHeaderStyle = {
    background: 'linear-gradient(135deg, #f97316 0%, #0d9488 100%)',
    color: '#fff',
    padding: '16px 24px',
    borderRadius: '16px',
    marginBottom: '20px',
    position: 'relative',
    overflow: 'hidden',
    minHeight: '120px',
    boxShadow: '0 10px 30px rgba(102, 126, 234, 0.25)'
  };

  const headerPatternStyle = {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    opacity: 0.1,
    backgroundImage: `
      radial-gradient(circle at 20% 50%, rgba(255,255,255,0.2) 0%, transparent 50%),
      radial-gradient(circle at 80% 80%, rgba(255,255,255,0.2) 0%, transparent 50%)
    `
  };

  const gridStyle = { display: 'grid', gap: '15px' };

  return (
    <>
      <style>{`
        .association-my-profile-page .form-label {
          display: block;
          font-size: 14px;
          font-weight: 600;
          color: #374151;
          margin-bottom: 6px;
        }

        .association-my-profile-page input,
        .association-my-profile-page textarea,
        .association-my-profile-page select {
          width: 100%;
          padding: 10px 12px;
          border-radius: 8px;
          border: 2px solid #e5e7eb;
          font-size: 14px;
          transition: all 0.3s ease;
          outline: none;
        }

        .association-my-profile-page input:focus,
        .association-my-profile-page textarea:focus,
        .association-my-profile-page select:focus {
          border-color: #f97316 !important;
          box-shadow: 0 0 0 3px rgba(249, 115, 22, 0.1) !important;
        }
      `}</style>

      <div
        className="association-my-profile-page"
        style={{
          display: 'flex',
          minHeight: '100vh',
          width: '100%',
          backgroundColor: '#f8fafc'
        }}
      >
        {/* Sidebar */}
        <div
          style={{
            width: '280px',
            background: 'linear-gradient(180deg, #f97316 0%, #0d9488 100%)',
            padding: '2rem 1.5rem',
            boxShadow: '4px 0 20px rgba(249, 115, 22, 0.15)',
            position: 'fixed',
            left: 0,
            top: 0,
            height: '100vh',
            overflowY: 'auto',
            zIndex: 1000,
            color: '#ffffff'
          }}
        >
          <div style={{ marginBottom: '2rem' }}>
            <h2
              style={{
                color: '#ffffff',
                fontSize: '1.5rem',
                fontWeight: '800',
                marginBottom: '0.5rem',
                textShadow: '0 4px 8px rgba(0,0,0,0.7)',
                letterSpacing: '0.5px'
              }}
            >
              Association Profile
            </h2>
            <p
              style={{
                color: '#ffffff',
                fontSize: '0.9rem',
                margin: 0,
                fontWeight: '700',
                textShadow: '0 2px 4px rgba(0,0,0,0.6)'
              }}
            >
              Manage your association information
            </p>
          </div>

          <div style={{ marginBottom: '2rem' }}>
            <h3
              style={{
                color: '#ffffff',
                fontSize: '1.1rem',
                fontWeight: '700',
                marginBottom: '1rem',
                textShadow: '0 3px 6px rgba(0,0,0,0.7)',
                letterSpacing: '0.3px'
              }}
            >
              Quick Navigation
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              <button
                type="button"
                onClick={() =>
                  document.querySelector('[data-section="association"]')?.scrollIntoView({ behavior: 'smooth' })
                }
                style={{
                  background: 'rgba(0,0,0,0.3)',
                  color: 'white',
                  border: '2px solid rgba(255,255,255,0.3)',
                  padding: '10px 15px',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '600',
                  textAlign: 'left',
                  transition: 'all 0.3s ease'
                }}
              >
                <FontAwesomeIcon icon={faBuilding} /> Association Information
              </button>
              <button
                type="button"
                onClick={() =>
                  document.querySelector('[data-section="contact"]')?.scrollIntoView({ behavior: 'smooth' })
                }
                style={{
                  background: 'rgba(0,0,0,0.3)',
                  color: 'white',
                  border: '2px solid rgba(255,255,255,0.3)',
                  padding: '10px 15px',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '600',
                  textAlign: 'left',
                  transition: 'all 0.3s ease'
                }}
              >
                <FontAwesomeIcon icon={faEnvelope} /> Contact Information
              </button>
              <button
                type="button"
                onClick={() =>
                  document.querySelector('[data-section="person"]')?.scrollIntoView({ behavior: 'smooth' })
                }
                style={{
                  background: 'rgba(0,0,0,0.3)',
                  color: 'white',
                  border: '2px solid rgba(255,255,255,0.3)',
                  padding: '10px 15px',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '600',
                  textAlign: 'left',
                  transition: 'all 0.3s ease'
                }}
              >
                <FontAwesomeIcon icon={faUsers} /> Contact Person
              </button>
              <button
                type="button"
                onClick={() =>
                  document.querySelector('[data-section="details"]')?.scrollIntoView({ behavior: 'smooth' })
                }
                style={{
                  background: 'rgba(0,0,0,0.3)',
                  color: 'white',
                  border: '2px solid rgba(255,255,255,0.3)',
                  padding: '10px 15px',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '600',
                  textAlign: 'left',
                  transition: 'all 0.3s ease'
                }}
              >
                <FontAwesomeIcon icon={faInfoCircle} /> Association Details
              </button>
              <button
                type="button"
                onClick={() =>
                  document.querySelector('[data-section="industries"]')?.scrollIntoView({ behavior: 'smooth' })
                }
                style={{
                  background: 'rgba(0,0,0,0.3)',
                  color: 'white',
                  border: '2px solid rgba(255,255,255,0.3)',
                  padding: '10px 15px',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '600',
                  textAlign: 'left',
                  transition: 'all 0.3s ease'
                }}
              >
                <FontAwesomeIcon icon={faBriefcase} /> Industries Served
              </button>
              <button
                type="button"
                onClick={() =>
                  document.querySelector('[data-section="social"]')?.scrollIntoView({ behavior: 'smooth' })
                }
                style={{
                  background: 'rgba(0,0,0,0.3)',
                  color: 'white',
                  border: '2px solid rgba(255,255,255,0.3)',
                  padding: '10px 15px',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '600',
                  textAlign: 'left',
                  transition: 'all 0.3s ease'
                }}
              >
                <FontAwesomeIcon icon={faGlobe} /> Social & Online
              </button>
            </div>
          </div>

          {/* Profile Completion */}
          <div
            style={{
              background: 'rgba(255,255,255,0.15)',
              backdropFilter: 'blur(10px)',
              padding: '20px',
              borderRadius: '12px',
              border: '2px solid rgba(255,255,255,0.2)'
            }}
          >
            <h4
              style={{
                color: '#ffffff',
                fontSize: '1rem',
                fontWeight: '700',
                marginBottom: '12px',
                textShadow: '0 2px 4px rgba(0,0,0,0.5)'
              }}
            >
              Profile Completion
            </h4>
            <div
              style={{
                background: 'rgba(0,0,0,0.2)',
                borderRadius: '10px',
                height: '10px',
                overflow: 'hidden',
                marginBottom: '10px'
              }}
            >
              <div
                style={{
                  background: 'linear-gradient(90deg, #10b981 0%, #14b8a6 100%)',
                  height: '100%',
                  width: `${profileCompletionPercentage}%`,
                  transition: 'width 0.5s ease',
                  boxShadow: '0 0 10px rgba(16, 185, 129, 0.8)'
                }}
              ></div>
            </div>
            <p
              style={{
                color: '#ffffff',
                fontSize: '14px',
                margin: '0 0 15px 0',
                textShadow: '0 1px 2px rgba(0,0,0,0.5)',
                fontWeight: '600'
              }}
            >
              {profileCompletionPercentage}% Complete
            </p>
            <button
              type="button"
              onClick={() => navigate('/association-registration')}
              style={{
                width: '100%',
                background: 'rgba(255, 255, 255, 0.25)',
                border: '2px solid rgba(255, 255, 255, 0.4)',
                color: '#ffffff',
                padding: '12px 16px',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: '700',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                textShadow: '0 2px 4px rgba(0,0,0,0.3)',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)'
              }}
              onMouseOver={(e) => {
                e.target.style.background = 'rgba(255, 255, 255, 0.35)';
                e.target.style.transform = 'translateY(-2px)';
                e.target.style.boxShadow = '0 6px 16px rgba(0, 0, 0, 0.3)';
              }}
              onMouseOut={(e) => {
                e.target.style.background = 'rgba(255, 255, 255, 0.25)';
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.2)';
              }}
            >
              <FontAwesomeIcon icon={faEdit} style={{ marginRight: '8px' }} />
              Complete Profile
            </button>
          </div>
        </div>

        {/* Main content area */}
        <div
          style={{
            marginLeft: '280px',
            flex: 1,
            padding: '40px',
            width: 'calc(100% - 280px)',
            overflowX: 'hidden',
            overflowY: 'auto',
            minHeight: '100vh',
            boxSizing: 'border-box',
            backgroundColor: '#f8fafc'
          }}
        >
          <div style={containerStyle}>
            {/* Header / hero */}
            <div style={profileHeaderStyle}>
              <div style={headerPatternStyle}></div>

              <button
                onClick={() => navigate('/association-dashboard')}
                style={{
                  position: 'absolute',
                  top: '20px',
                  right: '20px',
                  zIndex: 10,
                  background: 'rgba(255, 255, 255, 0.25)',
                  border: '2px solid rgba(255, 255, 255, 0.4)',
                  color: '#ffffff',
                  padding: '10px 20px',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)',
                  transition: 'all 0.3s ease'
                }}
              >
                <FontAwesomeIcon icon={faArrowLeft} />
                Back to Dashboard
              </button>

              <div style={{ display: 'flex', alignItems: 'center', gap: '30px', position: 'relative', zIndex: 2 }}>
                <div style={{ position: 'relative' }}>
                  <div
                    style={{
                      width: '76px',
                      height: '76px',
                      borderRadius: '50%',
                      background: 'rgba(255,255,255,0.25)',
                      backdropFilter: 'blur(10px)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '30px',
                      fontWeight: '700',
                      color: '#ffffff',
                      border: '3px solid rgba(255,255,255,0.4)',
                      boxShadow: '0 8px 24px rgba(0,0,0,0.35)',
                      textShadow: '0 4px 8px rgba(0,0,0,0.5)',
                      position: 'relative',
                      overflow: 'hidden'
                    }}
                  >
                    {(associationLogoPreview || profileForm.associationLogo) ? (() => {
                      let imageUrl = '';
                      if (associationLogoPreview) {
                        imageUrl = associationLogoPreview;
                      } else {
                        const normalizedPath = profileForm.associationLogo.replace(/\\/g, '/');
                        if (normalizedPath.startsWith('uploads/')) {
                          const uploadPath = normalizedPath.replace('uploads/', '');
                          imageUrl = buildApiUrl(`/api/users/uploads/${uploadPath}`);
                        } else if (normalizedPath.startsWith('/uploads/')) {
                          const uploadPath = normalizedPath.replace('/uploads/', '');
                          imageUrl = buildApiUrl(`/api/users/uploads/${uploadPath}`);
                        } else if (normalizedPath.startsWith('http://') || normalizedPath.startsWith('https://')) {
                          imageUrl = normalizedPath;
                        } else {
                          imageUrl = buildApiUrl(`/api/users/uploads/${normalizedPath}`);
                        }
                      }
                      return (
                        <img 
                          src={imageUrl}
                          alt={profileForm.associationName || 'Association'}
                          style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            borderRadius: '50%'
                          }}
                          onError={(e) => {
                            e.target.style.display = 'none';
                          }}
                        />
                      );
                    })() : null}
                    <div style={{
                      display: (associationLogoPreview || profileForm.associationLogo) ? 'none' : 'flex',
                      width: '100%',
                      height: '100%',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <FontAwesomeIcon icon={faBuilding} />
                    </div>
                  </div>
                  {/* Camera button overlay */}
                  <label
                    style={{
                      position: 'absolute',
                      bottom: '0',
                      right: '0',
                      width: '28px',
                      height: '28px',
                      borderRadius: '50%',
                      background: 'linear-gradient(135deg, #f97316 0%, #0d9488 100%)',
                      border: '3px solid #ffffff',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
                      zIndex: 10
                    }}
                    title="Upload association logo"
                  >
                    <FontAwesomeIcon icon={faCamera} style={{ color: '#ffffff', fontSize: '12px' }} />
                    <input
                      type="file"
                      ref={associationLogoInputRef}
                      accept="image/png,image/jpeg,image/jpg"
                      onChange={handleAssociationLogoChange}
                      style={{ display: 'none' }}
                    />
                  </label>
                </div>

                <div style={{ flex: 1 }}>
                  <h2
                    style={{
                      marginBottom: '4px',
                      fontSize: '28px',
                      fontWeight: '700',
                      textShadow: '0 2px 4px rgba(0,0,0,0.3)',
                      color: '#ffffff'
                    }}
                  >
                    {profileForm.associationName || 'Association Name'}
                  </h2>
                  <p
                    style={{
                      fontSize: '16px',
                      fontWeight: '500',
                      textShadow: '0 1px 2px rgba(0,0,0,0.3)',
                      color: '#ffffff',
                      marginTop: '2px',
                      marginBottom: 0
                    }}
                  >
                    <FontAwesomeIcon icon={faMapMarkerAlt} />{' '}
                    {primaryLocation || 'Location not set'} ·{' '}
                    <FontAwesomeIcon icon={faUsers} />{' '}
                    {profileForm.totalMembers || '0'} Members
                  </p>
                </div>
              </div>
            </div>

            {/* Section 1: Association Information */}
            <div style={elevatedCardStyle} data-section="association">
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '20px',
                  paddingBottom: '15px',
                  borderBottom: '2px solid #f0f0f0'
                }}
              >
                <h3
                  style={{
                    fontSize: '20px',
                    fontWeight: '700',
                    color: '#1e293b',
                    margin: 0
                  }}
                >
                  <FontAwesomeIcon icon={faBuilding} style={{ color: '#f97316', marginRight: '10px' }} />
                  Association Information
                </h3>
                <EditButton section="association" />
              </div>

              <div style={{ ...gridStyle, gridTemplateColumns: 'repeat(2, 1fr)', marginBottom: '15px' }}>
                <div className="form-group">
                  <label className="form-label">Association Name</label>
                  <input
                    type="text"
                    name="associationName"
                    value={profileForm.associationName}
                    onChange={handleChange}
                    disabled={!editingSections.association}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Association Type</label>
                  <select
                    name="associationType"
                    value={profileForm.associationType}
                    onChange={handleChange}
                    disabled={!editingSections.association}
                  >
                    <option value="">Select Type</option>
                    {ASSOCIATION_TYPES.map((type) => (
                      <option key={type} value={type}>
                        {type}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div style={{ ...gridStyle, gridTemplateColumns: 'repeat(3, 1fr)' }}>
                <div className="form-group">
                  <label className="form-label">Registration Number</label>
                  <input
                    type="text"
                    name="registrationNumber"
                    value={profileForm.registrationNumber}
                    onChange={handleChange}
                    disabled={!editingSections.association}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Year Established</label>
                  <input
                    type="number"
                    name="yearEstablished"
                    value={profileForm.yearEstablished}
                    onChange={handleChange}
                    disabled={!editingSections.association}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Website</label>
                  <input
                    type="url"
                    name="website"
                    value={profileForm.website}
                    onChange={handleChange}
                    disabled={!editingSections.association}
                  />
                </div>
              </div>

              <div style={{ marginTop: '20px', padding: '15px', background: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                <div className="form-group" style={{ marginBottom: '15px' }}>
                  <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
                    <input
                      type="checkbox"
                      name="showLogoOnWebsite"
                      checked={profileForm.showLogoOnWebsite !== false}
                      onChange={(e) =>
                        setProfileForm(prev => ({
                          ...prev,
                          showLogoOnWebsite: e.target.checked
                        }))
                      }
                      disabled={!editingSections.association}
                      style={{ width: '16px', height: '16px', cursor: editingSections.association ? 'pointer' : 'default' }}
                    />
                    <span style={{ fontWeight: '500' }}>Show our association logo on the Associations page</span>
                  </label>
                  <p style={{ margin: '5px 0 0 24px', fontSize: '12px', color: '#64748b' }}>
                    When enabled, your association logo will be displayed on the public Associations page.
                  </p>
                </div>
                
                {editingSections.association && (
                  <div className="form-group">
                    <label style={{ fontSize: '13px', fontWeight: '600', color: '#1e293b', marginBottom: '8px', display: 'block' }}>
                      Upload Association Logo
                    </label>
                    {associationLogoPreview && (
                      <div style={{ marginBottom: '10px', textAlign: 'center' }}>
                        <img 
                          src={associationLogoPreview} 
                          alt="Association Logo Preview" 
                          style={{ maxWidth: '200px', maxHeight: '100px', objectFit: 'contain', borderRadius: '4px', border: '1px solid #e2e8f0' }}
                        />
                      </div>
                    )}
                    <label className="file-upload-label" style={{ cursor: 'pointer', padding: '12px', border: '1px dashed #cbd5e1', borderRadius: '6px', background: '#fff', display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <FontAwesomeIcon icon={faImage} style={{ fontSize: '20px', color: '#f97316' }} />
                      <div style={{ flex: 1 }}>
                        <strong style={{ fontSize: '13px', display: 'block', marginBottom: '2px' }}>Choose Logo File</strong>
                        <p style={{ margin: 0, fontSize: '11px', color: '#64748b' }}>PNG, JPG (max 5MB)</p>
                      </div>
                      <input 
                        type="file" 
                        ref={associationLogoInputRef}
                        accept="image/png,image/jpeg,image/jpg" 
                        onChange={handleAssociationLogoChange}
                        style={{ display: 'none' }}
                      />
                    </label>
                  </div>
                )}
              </div>
            </div>

            {/* Section 2: Contact Information */}
            <div style={elevatedCardStyle} data-section="contact">
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '20px',
                  paddingBottom: '15px',
                  borderBottom: '2px solid #f0f0f0'
                }}
              >
                <h3
                  style={{
                    fontSize: '20px',
                    fontWeight: '700',
                    color: '#1e293b',
                    margin: 0
                  }}
                >
                  <FontAwesomeIcon icon={faEnvelope} style={{ color: '#f97316', marginRight: '10px' }} />
                  Contact Information
                </h3>
                <EditButton section="contact" />
              </div>

              <div style={{ ...gridStyle, gridTemplateColumns: 'repeat(2, 1fr)', marginBottom: '15px' }}>
                <div className="form-group">
                  <label className="form-label">Email Address</label>
                  <input
                    type="email"
                    name="email"
                    value={profileForm.email}
                    onChange={handleChange}
                    disabled={!editingSections.contact}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">
                    <FontAwesomeIcon icon={faPhone} /> Phone Number
                  </label>
                  <input
                    type="text"
                    name="phone"
                    value={profileForm.phone}
                    onChange={handleChange}
                    disabled={!editingSections.contact}
                  />
                </div>
              </div>

              <div style={{ ...gridStyle, gridTemplateColumns: '1fr', marginBottom: '15px' }}>
                <div className="form-group">
                  <label className="form-label">Address</label>
                  <input
                    type="text"
                    name="address"
                    value={profileForm.address}
                    onChange={handleChange}
                    disabled={!editingSections.contact}
                  />
                </div>
              </div>

              <div style={{ ...gridStyle, gridTemplateColumns: 'repeat(4, 1fr)' }}>
                <div className="form-group">
                  <label className="form-label">City</label>
                  <input
                    type="text"
                    name="city"
                    value={profileForm.city}
                    onChange={handleChange}
                    disabled={!editingSections.contact}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">State/County</label>
                  <input
                    type="text"
                    name="state"
                    value={profileForm.state}
                    onChange={handleChange}
                    disabled={!editingSections.contact}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Country</label>
                  <input
                    type="text"
                    name="country"
                    value={profileForm.country}
                    onChange={handleChange}
                    disabled={!editingSections.contact}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Postal Code</label>
                  <input
                    type="text"
                    name="postalCode"
                    value={profileForm.postalCode}
                    onChange={handleChange}
                    disabled={!editingSections.contact}
                  />
                </div>
              </div>
            </div>

            {/* Section 3: Primary Contact Person */}
            <div style={elevatedCardStyle} data-section="person">
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '20px',
                  paddingBottom: '15px',
                  borderBottom: '2px solid #f0f0f0'
                }}
              >
                <h3
                  style={{
                    fontSize: '20px',
                    fontWeight: '700',
                    color: '#1e293b',
                    margin: 0
                  }}
                >
                  Primary Contact Person
                </h3>
                <EditButton section="person" />
              </div>

              <div style={{ ...gridStyle, gridTemplateColumns: 'repeat(2, 1fr)', marginBottom: '15px' }}>
                <div className="form-group">
                  <label className="form-label">Full Name</label>
                  <input
                    type="text"
                    name="contactName"
                    value={profileForm.contactName}
                    onChange={handleChange}
                    disabled={!editingSections.person}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Designation</label>
                  <input
                    type="text"
                    name="designation"
                    value={profileForm.designation}
                    onChange={handleChange}
                    disabled={!editingSections.person}
                  />
                </div>
              </div>

              <div style={{ ...gridStyle, gridTemplateColumns: 'repeat(2, 1fr)' }}>
                <div className="form-group">
                  <label className="form-label">Contact Email</label>
                  <input
                    type="email"
                    name="contactEmail"
                    value={profileForm.contactEmail}
                    onChange={handleChange}
                    disabled={!editingSections.person}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Contact Phone</label>
                  <input
                    type="text"
                    name="contactPhone"
                    value={profileForm.contactPhone}
                    onChange={handleChange}
                    disabled={!editingSections.person}
                  />
                </div>
              </div>
            </div>

            {/* Section 4: Association Details */}
            <div style={elevatedCardStyle} data-section="details">
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '20px',
                  paddingBottom: '15px',
                  borderBottom: '2px solid #f0f0f0'
                }}
              >
                <h3
                  style={{
                    fontSize: '20px',
                    fontWeight: '700',
                    color: '#1e293b',
                    margin: 0
                  }}
                >
                  <FontAwesomeIcon icon={faInfoCircle} style={{ color: '#f97316', marginRight: '10px' }} />
                  Association Details
                </h3>
                <EditButton section="details" />
              </div>

              <div style={{ ...gridStyle, gridTemplateColumns: 'repeat(3, 1fr)', marginBottom: '15px' }}>
                <div className="form-group">
                  <label className="form-label">Total Members</label>
                  <input
                    type="number"
                    name="totalMembers"
                    value={profileForm.totalMembers}
                    onChange={handleChange}
                    disabled={!editingSections.details}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Total Staff</label>
                  <input
                    type="number"
                    name="totalStaff"
                    value={profileForm.totalStaff}
                    onChange={handleChange}
                    disabled={!editingSections.details}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Accreditation Body</label>
                  <input
                    type="text"
                    name="accreditation"
                    value={profileForm.accreditation}
                    onChange={handleChange}
                    disabled={!editingSections.details}
                  />
                </div>
              </div>

              <div style={{ ...gridStyle, gridTemplateColumns: '1fr', marginBottom: '15px' }}>
                <div className="form-group">
                  <label className="form-label">Accreditation Number</label>
                  <input
                    type="text"
                    name="accreditationNumber"
                    value={profileForm.accreditationNumber}
                    onChange={handleChange}
                    disabled={!editingSections.details}
                  />
                </div>
              </div>

              <div style={{ ...gridStyle, gridTemplateColumns: '1fr', marginBottom: '15px' }}>
                <div className="form-group">
                  <label className="form-label">Member Benefits</label>
                  <textarea
                    name="memberBenefits"
                    value={profileForm.memberBenefits}
                    onChange={handleChange}
                    disabled={!editingSections.details}
                    rows={3}
                    placeholder="Describe the benefits and services your association provides to members"
                  />
                </div>
              </div>

              <div style={{ ...gridStyle, gridTemplateColumns: '1fr' }}>
                <div className="form-group">
                  <label className="form-label">About the Association</label>
                  <textarea
                    name="about"
                    value={profileForm.about}
                    onChange={handleChange}
                    disabled={!editingSections.details}
                    rows={4}
                    placeholder="Brief description of your association, mission, achievements, and unique features"
                  />
                </div>
              </div>
            </div>

            {/* Section 5: Industries Served */}
            <div style={elevatedCardStyle} data-section="industries">
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '20px',
                  paddingBottom: '15px',
                  borderBottom: '2px solid #f0f0f0'
                }}
              >
                <h3
                  style={{
                    fontSize: '20px',
                    fontWeight: '700',
                    color: '#1e293b',
                    margin: 0
                  }}
                >
                  <FontAwesomeIcon icon={faBriefcase} style={{ color: '#f97316', marginRight: '10px' }} />
                  Industries Served
                </h3>
                <EditButton section="industries" />
              </div>

              <div style={{ ...gridStyle, gridTemplateColumns: '1fr' }}>
                <div className="form-group">
                  <label className="form-label">Industries / Sectors Served</label>
                  <textarea
                    name="industriesServed"
                    value={Array.isArray(profileForm.industriesServed) 
                      ? profileForm.industriesServed.join(', ') 
                      : (profileForm.industriesServed || '')}
                    onChange={(e) => {
                      const value = e.target.value;
                      const industriesArray = value.split(',').map(i => i.trim()).filter(i => i);
                      setProfileForm(prev => ({
                        ...prev,
                        industriesServed: industriesArray
                      }));
                    }}
                    disabled={!editingSections.industries}
                    rows={3}
                    placeholder="Enter industries separated by commas (e.g., Education & Training, Non-profit & Social Services, Community Development)"
                  />
                  <p style={{ marginTop: '5px', fontSize: '12px', color: '#64748b' }}>
                    Separate multiple industries with commas
                  </p>
                </div>
              </div>
            </div>

            {/* Section 6: Social & Online Presence */}
            <div style={elevatedCardStyle} data-section="social">
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '20px',
                  paddingBottom: '15px',
                  borderBottom: '2px solid #f0f0f0'
                }}
              >
                <h3
                  style={{
                    fontSize: '20px',
                    fontWeight: '700',
                    color: '#1e293b',
                    margin: 0
                  }}
                >
                  <FontAwesomeIcon icon={faGlobe} style={{ color: '#f97316', marginRight: '10px' }} />
                  Social Media & Online Presence
                </h3>
                <EditButton section="social" />
              </div>

              <div style={{ ...gridStyle, gridTemplateColumns: 'repeat(2, 1fr)', marginBottom: '15px' }}>
                <div className="form-group">
                  <label className="form-label">
                    <FontAwesomeIcon icon={faLinkedin} /> LinkedIn
                  </label>
                  <input
                    type="url"
                    name="linkedin"
                    value={profileForm.linkedin}
                    onChange={handleChange}
                    disabled={!editingSections.social}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">
                    <FontAwesomeIcon icon={faFacebook} /> Facebook
                  </label>
                  <input
                    type="url"
                    name="facebook"
                    value={profileForm.facebook}
                    onChange={handleChange}
                    disabled={!editingSections.social}
                  />
                </div>
              </div>

              <div style={{ ...gridStyle, gridTemplateColumns: 'repeat(2, 1fr)' }}>
                <div className="form-group">
                  <label className="form-label">
                    <FontAwesomeIcon icon={faTwitter} /> Twitter / X
                  </label>
                  <input
                    type="text"
                    name="twitter"
                    value={profileForm.twitter}
                    onChange={handleChange}
                    disabled={!editingSections.social}
                    placeholder="@username"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">
                    <FontAwesomeIcon icon={faInstagram} /> Instagram
                  </label>
                  <input
                    type="text"
                    name="instagram"
                    value={profileForm.instagram}
                    onChange={handleChange}
                    disabled={!editingSections.social}
                    placeholder="@username"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default AssociationMyProfile;

